import { cn } from '@/lib/utils'

import { TaskPriorityEnum } from '@scrambl/shared'

const LEVEL: Partial<Record<string, number>> = {
  [TaskPriorityEnum.LOW]: 1,
  [TaskPriorityEnum.MEDIUM]: 2,
  [TaskPriorityEnum.HIGH]: 3,
}

// A cool-to-hot ladder that ends in Urgent's red.
const TONE: Partial<Record<string, string>> = {
  [TaskPriorityEnum.LOW]: 'bg-slate-500 dark:bg-slate-400',
  [TaskPriorityEnum.MEDIUM]: 'bg-blue-500',
  [TaskPriorityEnum.HIGH]: 'bg-orange-500',
}

// Whole-pixel geometry so bars render crisply at both sizes.
const GEOMETRY = {
  sm: {
    box: 'h-3.5 w-3.5',
    bar: 'w-[3px]',
    gap: 'gap-[2px]',
    heights: [5, 8, 12],
  },
  md: {
    box: 'h-4 w-4',
    bar: 'w-[4px]',
    gap: 'gap-[2px]',
    heights: [6, 10, 14],
  },
} as const

/**
 * The one priority mark for tasks and tickets: ascending signal bars for
 * Low/Medium/High (one, two, three filled) and a solid red tile for Urgent,
 * so every step reads as stronger than the last. A missing priority renders
 * as Medium (tasks' default) unless `unsetAsNone`, which draws empty bars
 * (tickets may have no priority).
 */
export function PriorityGlyph({
  priority,
  size = 'md',
  unsetAsNone = false,
  className,
}: {
  priority: string | null | undefined
  size?: 'sm' | 'md'
  unsetAsNone?: boolean
  className?: string
}) {
  const geometry = GEOMETRY[size]

  if (priority === TaskPriorityEnum.URGENT) {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-flex shrink-0 flex-col items-center justify-center gap-[1.5px] rounded-[4px] bg-red-500 shadow-[inset_0_-1px_0_rgba(0,0,0,0.12)]',
          geometry.box,
          className,
        )}
      >
        {/* Drawn rather than an icon glyph so the mark stays crisp at 14px. */}
        <span
          className={cn(
            'rounded-full bg-white',
            size === 'sm' ? 'h-[6px] w-[2px]' : 'h-[7px] w-[2px]',
          )}
        />
        <span className="h-[2px] w-[2px] rounded-full bg-white" />
      </span>
    )
  }

  const isKnown = Boolean(priority && LEVEL[priority])
  const key = isKnown ? priority! : TaskPriorityEnum.MEDIUM
  const level = !isKnown && unsetAsNone ? 0 : (LEVEL[key] ?? 2)
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-end justify-center pb-px',
        geometry.box,
        geometry.gap,
        className,
      )}
    >
      {geometry.heights.map((height, index) => (
        <span
          key={height}
          style={{ height }}
          className={cn(
            'rounded-[1.5px]',
            geometry.bar,
            index < level ? TONE[key] : 'bg-slate-200 dark:bg-slate-700',
          )}
        />
      ))}
    </span>
  )
}

/** Glyph followed by its label, as priority reads in rows, cards and menus. */
export function PriorityLabel({
  priority,
  label,
  size = 'md',
  unsetAsNone = false,
  className,
  labelClassName,
}: {
  priority: string | null | undefined
  label: string
  size?: 'sm' | 'md'
  unsetAsNone?: boolean
  className?: string
  labelClassName?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center',
        size === 'sm' ? 'gap-1' : 'gap-1.5',
        className,
      )}
    >
      <PriorityGlyph
        priority={priority}
        size={size}
        unsetAsNone={unsetAsNone}
      />
      <span
        className={cn(
          'truncate',
          size === 'sm' ? 'text-xs' : 'text-sm',
          labelClassName,
        )}
      >
        {label}
      </span>
    </span>
  )
}
